import { AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AuthPage } from '../features/authentication';
import {
  CartDrawer,
  CartPage,
  CheckoutPage,
  CheckoutPaymentCallbackPage,
  CheckoutPaymentPage,
} from '../features/checkout';
import {
  AdminDashboardPage,
  BuyerAudioRecorderPage,
  BuyerDashboardPage,
  BuyerVideoRecorderPage,
  PodBookDetailsPage,
  PrintOnDemandPage,
  VendorAddProductPage,
  VendorDashboardPage,
  VendorProductsManagerPage,
  BuyerTransactionHistoryPage,
  VendorTransferPage,
} from '../features/dashboard';
import { AboutPage, AppComingSoonPage, ContactPage, HomePage, PrivacyPolicyPage, PublishersPage, PublisherProfilePage, TermsOfUsePage } from '../features/marketing';
import { ProfilePage } from '../features/profile';
import { BookPageViewPage, ProductDetailsPage, ProductsPage, PDFReaderPage } from '../features/products';
import { BakameLoader, GlobalAudioMiniPlayer, HaapaAssistantWidget, SiteHeader } from '../shared/components';
import { GlobalAudioProvider, ShopProvider, useShop } from '../shared/store';
import { resolveAudienceFace } from '../shared/utils/audience';
import AutoDomTranslator from '../shared/i18n/AutoDomTranslator';
import { authService } from '../shared/services/authService';

const AppLayout = () => {
  const location = useLocation();
  const { themeMode, currentUser } = useShop();
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [kidTransitionMode, setKidTransitionMode] = useState<'login' | 'route' | null>(null);
  const previousPathRef = useRef(location.pathname);
  const hasMountedRef = useRef(false);
  const previousKidsFaceRef = useRef(false);
  const isHomePage = location.pathname === '/';
  const isDashboardPage =
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/admin/dashboard') ||
    location.pathname.startsWith('/vendor/dashboard') ||
    location.pathname.startsWith('/vendor/add-product') ||
    location.pathname.startsWith('/vendor/print-on-demand');
  const isAppComingSoonPage = location.pathname.startsWith('/apps/');
  const isPublicPodPage = location.pathname === '/pod';
  const shouldHideSiteHeader = isDashboardPage;
  const isFullBleedPage = isHomePage || location.pathname === '/about' || isDashboardPage || isAppComingSoonPage || isPublicPodPage;
  const useOverlayHeader = isHomePage;
  const isKidsFace = resolveAudienceFace(currentUser) === 'kids';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
  }, [themeMode]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousPathRef.current = location.pathname;
      previousKidsFaceRef.current = isKidsFace;
      return;
    }

    if (!isKidsFace) {
      setKidTransitionMode(null);
      previousPathRef.current = location.pathname;
      previousKidsFaceRef.current = false;
      return;
    }

    const wasKidsFace = previousKidsFaceRef.current;
    const hasPathChanged = previousPathRef.current !== location.pathname;
    const isLoginTransition = !wasKidsFace;

    if (!hasPathChanged && !isLoginTransition) {
      previousKidsFaceRef.current = true;
      return;
    }

    previousPathRef.current = location.pathname;
    previousKidsFaceRef.current = true;
    setKidTransitionMode(isLoginTransition ? 'login' : 'route');
    const timer = window.setTimeout(() => {
      setKidTransitionMode(null);
    }, isLoginTransition ? 1500 : 1100);

    return () => window.clearTimeout(timer);
  }, [isKidsFace, location.pathname]);

  return (
    <GlobalAudioProvider>
      <AutoDomTranslator />
      <div
        className={`min-h-screen ${
          isDashboardPage
            ? themeMode === 'dark'
              ? 'bg-[#090f1d]'
              : 'bg-[#fff3e8]'
            : themeMode === 'dark'
              ? 'bg-[#020617]'
              : 'bg-[#f8fbff]'
        }`}
      >
      {!shouldHideSiteHeader && (
        <SiteHeader
          isHomePage={useOverlayHeader}
          onOpenCart={() => setIsCartDrawerOpen(true)}
        />
      )}
      <main
        className={`${!shouldHideSiteHeader ? 'pb-28 md:pb-0' : ''} ${
          isDashboardPage
            ? 'mx-auto w-full px-2 py-2 sm:px-4 sm:py-4'
            : isFullBleedPage
              ? ''
              : 'mx-auto w-full max-w-7xl px-4 pb-8 pt-24 sm:px-6 lg:px-8'
        }`.trim()}
      >
        <Outlet />
      </main>
      {!shouldHideSiteHeader && <CartDrawer isOpen={isCartDrawerOpen} onClose={() => setIsCartDrawerOpen(false)} />}
      {!shouldHideSiteHeader && <GlobalAudioMiniPlayer />}
      <HaapaAssistantWidget />
      {kidTransitionMode && isKidsFace && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-gradient-to-br from-white/95 via-slate-900/40 to-slate-950/70 px-4 py-8 backdrop-blur-sm">
          <article className="w-full max-w-md rounded-3xl border border-orange-200 bg-white/95 p-5 text-center shadow-[0_25px_70px_-35px_rgba(249,115,22,0.8)] sm:p-6">
            <img
              src="/congratulation kids.gif"
              alt="Congratulation animation"
              className="mx-auto h-44 w-44 rounded-2xl object-cover sm:h-52 sm:w-52"
            />
            <h2 className="mt-4 text-2xl font-semibold text-slate-900">
              {kidTransitionMode === 'login' ? 'Congratulation' : 'Loading please wait'}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {kidTransitionMode === 'login'
                ? `Welcome ${currentUser?.name ?? 'Young Reader'}! Keep learning and keep reading.`
                : 'Preparing your next page...'}
            </p>
          </article>
        </div>
      )}
      </div>
    </GlobalAudioProvider>
  );
};

const BuyerRouteGuard = () => {
  const { currentUser } = useShop();
  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  return <Outlet />;
};

const AdminRouteGuard = () => {
  const { currentUser } = useShop();
  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'manager')) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!authService.getToken()) {
    return <Navigate to="/auth" replace />;
  }

  return <Outlet />;
};

const VendorRouteGuard = () => {
  const { currentUser } = useShop();
  if (!currentUser || currentUser.role !== 'vendor') {
    return <Navigate to="/auth" replace />;
  }

  return <Outlet />;
};

const AuthenticatedRouteGuard = () => {
  const { currentUser } = useShop();
  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  return <Outlet />;
};

const AppRoutes = () => (
  <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/apps/:platform" element={<AppComingSoonPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/publishers" element={<PublishersPage />} />
        <Route path="/publishers/:slug" element={<PublisherProfilePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
      <Route path="/pod" element={<PrintOnDemandPage />} />
      <Route path="/pod/:productId" element={<PodBookDetailsPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route path="/products" element={<Navigate to="/books" replace />} />
      <Route path="/books" element={<ProductsPage mediaType="book" />} />
      <Route path="/animations" element={<Navigate to="/animation" replace />} />
      <Route path="/animation" element={<ProductsPage mediaType="animation" />} />
      <Route path="/audio" element={<ProductsPage mediaType="audio" />} />
      <Route path="/products/:productId" element={<ProductDetailsPage />} />
      <Route path="/products/:productId/pageview" element={<BookPageViewPage />} />
      <Route path="/reader" element={<PDFReaderPage />} />
      <Route path="/admin/login" element={<Navigate to="/auth" replace />} />

      <Route element={<AuthenticatedRouteGuard />}>
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/payment" element={<CheckoutPaymentPage />} />
        <Route path="/checkout/payment/callback" element={<CheckoutPaymentCallbackPage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>

      <Route element={<BuyerRouteGuard />}>
        <Route path="/dashboard" element={<Navigate to="/dashboard/home" replace />} />
        <Route path="/dashboard/transactions" element={<BuyerTransactionHistoryPage />} />
        <Route path="/dashboard/create-content/audio-recorder" element={<BuyerAudioRecorderPage />} />
        <Route path="/dashboard/create-content/video-recorder" element={<BuyerVideoRecorderPage />} />
        <Route path="/dashboard/:section" element={<BuyerDashboardPage />} />
      </Route>

      <Route element={<AdminRouteGuard />}>
        <Route path="/admin/dashboard" element={<Navigate to="/admin/dashboard/overview" replace />} />
        <Route path="/admin/dashboard/:section" element={<AdminDashboardPage />} />
      </Route>

      <Route element={<VendorRouteGuard />}>
        <Route path="/vendor/add-product" element={<VendorAddProductPage />} />
        <Route path="/vendor/dashboard" element={<Navigate to="/vendor/dashboard/overview" replace />} />
        <Route path="/vendor/dashboard/products-manager" element={<VendorProductsManagerPage />} />
        <Route path="/vendor/dashboard/transfers" element={<VendorTransferPage />} />
        <Route path="/vendor/dashboard/:section" element={<VendorDashboardPage />} />
        <Route path="/vendor/print-on-demand" element={<PrintOnDemandPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  </Routes>
);

function App() {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <BrowserRouter>
      <ShopProvider>
        <AnimatePresence mode="wait">
          {isLoading ? (
            <BakameLoader key="bakame-loader" onLoadingComplete={() => setIsLoading(false)} redirectToHome={false} />
          ) : (
            <AppRoutes key="app-router" />
          )}
        </AnimatePresence>
      </ShopProvider>
    </BrowserRouter>
  );
}

export default App;
